import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

/**
 * Extracts YouTube video ID from various YouTube URL formats
 * @param {string} url - YouTube URL in any format
 * @returns {string|null} - YouTube video ID or null if not found
 */
export function extractYouTubeId(url) {
  if (!url) return null;
  
  // Handle various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
}

/**
 * Converts YouTube URLs to embeddable iframe format
 * @param {string} html - HTML content that may contain YouTube URLs
 * @returns {string} - HTML with YouTube URLs converted to responsive iframes
 */
export function processYouTubeEmbeds(html) {
  if (!html) return html;
  
  let processedContent = html;
  
  // First, handle YouTube URLs in anchor tags <a href="...youtube...">
  // Match <a> tags with YouTube URLs
  const linkPattern = /<a[^>]*href=["'](https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^"'\s&]+))["'][^>]*>([^<]*)<\/a>/gi;
  processedContent = processedContent.replace(linkPattern, (match, url, videoId, linkText) => {
    if (videoId) {
      // Extract clean video ID (remove any query parameters)
      const cleanVideoId = videoId.split('&')[0].split('?')[0];
      
      // Replace the link with a responsive YouTube iframe (wider than prose content)
      return `<div class="youtube-embed-wrapper my-8" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;">
        <iframe 
          src="https://www.youtube.com/embed/${cleanVideoId}" 
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
          allowfullscreen
          class="rounded-lg"
        ></iframe>
      </div>`;
    }
    return match;
  });
  
  // Handle plain text YouTube URLs (not wrapped in HTML tags)
  // Match YouTube URLs that are not inside HTML tags
  const plainUrlPattern = /(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s<"'>]+))/g;
  processedContent = processedContent.replace(plainUrlPattern, (match, url, videoId, offset, string) => {
    // Check if this URL is already inside an iframe, link, or other HTML tag
    const beforeMatch = string.substring(Math.max(0, offset - 100), offset);
    const afterMatch = string.substring(offset + match.length, Math.min(string.length, offset + match.length + 100));
    
    // Skip if already in an iframe, link, or other HTML tag
    if (beforeMatch.includes('<iframe') || beforeMatch.includes('<a') || 
        beforeMatch.includes('youtube-embed-wrapper') ||
        beforeMatch.match(/<[^>]*href=["'][^"']*$/)) { // Check if inside an attribute
      return match;
    }
    
    // Skip if it's part of an already processed embed
    if (afterMatch.includes('</iframe>') || afterMatch.includes('</a>') ||
        afterMatch.includes('youtube-embed-wrapper')) {
      return match;
    }
    
    // Extract clean video ID (remove any query parameters from videoId)
    const cleanVideoId = videoId.split('&')[0].split('?')[0];
    
    // Create responsive iframe embed (wider than prose content)
    return `<div class="youtube-embed-wrapper my-8" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;">
      <iframe 
        src="https://www.youtube.com/embed/${cleanVideoId}" 
        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen
        class="rounded-lg"
      ></iframe>
    </div>`;
  });
  
  // Make existing YouTube iframes responsive (wrap them if not already wrapped)
  const iframePattern = /<iframe[^>]*src=["'](https?:\/\/(?:www\.)?(?:youtube\.com\/embed\/|youtu\.be\/)([^"'\s&]+))["'][^>]*>.*?<\/iframe>/gi;
  processedContent = processedContent.replace(iframePattern, (match, src, videoId) => {
    // Check if already wrapped in responsive container
    if (match.includes('youtube-embed-wrapper')) {
      return match;
    }
    
    // Extract the iframe tag and its attributes
    const iframeMatch = match.match(/<iframe[^>]*>/);
    if (!iframeMatch) return match;
    
    let iframeTag = iframeMatch[0];
    // Update iframe style to be absolute positioned
    if (iframeTag.includes('style=')) {
      iframeTag = iframeTag.replace(/style=["'][^"']*["']/, 'style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"');
    } else {
      iframeTag = iframeTag.replace(/>/, ' style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;">');
    }
    
    // Wrap existing iframe (wider than prose content)
    return `<div class="youtube-embed-wrapper my-8" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;">
      ${iframeTag}${match.substring(iframeMatch[0].length)}
    </div>`;
  });
  
  // Also handle standalone iframe tags (self-closing or with closing tag)
  const standaloneIframePattern = /(<iframe[^>]*src=["'](https?:\/\/(?:www\.)?(?:youtube\.com\/embed\/|youtu\.be\/)([^"'\s&]+))["'][^>]*\/?>)/gi;
  processedContent = processedContent.replace(standaloneIframePattern, (match, iframeTag, src, videoId) => {
    // Check if already wrapped
    const beforeMatch = processedContent.substring(0, processedContent.indexOf(match));
    if (beforeMatch.includes('youtube-embed-wrapper') && 
        beforeMatch.lastIndexOf('youtube-embed-wrapper') > beforeMatch.lastIndexOf('</div>')) {
      return match;
    }
    
    // Update iframe style
    let updatedIframe = iframeTag;
    if (updatedIframe.includes('style=')) {
      updatedIframe = updatedIframe.replace(/style=["'][^"']*["']/, 'style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"');
    } else {
      updatedIframe = updatedIframe.replace(/(\/?>)$/, ' style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"$1');
    }
    
    return `<div class="youtube-embed-wrapper my-8" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;">
      ${updatedIframe}
    </div>`;
  });
  
  return processedContent;
}